import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment/min/moment-with-locales';
import { updateMe, setLanguage, setCountry, setOrderSupportConfig, setOrderSupportCheckList } from 'Actions';
import { useLocation, useNavigate } from 'react-router-dom';
import Api from 'Api';
import { setAllFieldList, setDefaultField } from 'Actions/ConfigActions';
import { checkPermission } from 'Util/Permissions';
import { logoutUser } from 'Actions/AuthActions';

const useMainApp = () => {
  const { user, token } = useSelector(store => store.authUser);
  const { locale } = useSelector(store => store.settings);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loadingRef = useRef(false);

  const setCountryVietnam = useCallback(async () => {
    dispatch(setCountry('vi'));
    dispatch(
      setLanguage({
        languageId: 'vietnamese',
        locale: 'vi',
        name: 'Vietnamese',
        icon: 'vi',
      })
    );
    moment.locale('vi');
  }, [dispatch]);

  const setCountryKorea = useCallback(async () => {
    dispatch(setCountry('ko'));
    dispatch(
      setLanguage({
        languageId: 'korean',
        locale: 'ko',
        name: 'Korean',
        icon: 'ko',
      })
    );
    moment.locale('ko');
  }, [dispatch]);

  const setInitLanguage = useCallback(async () => {
    const browserLanguage = (navigator.languages && navigator.languages[0]) || navigator.language || navigator.userLanguage;
    if (browserLanguage === 'vi') {
      setCountryVietnam();
    } else if (browserLanguage === 'ko_KR') {
      setCountryKorea();
    } else {
      const countryCode = localStorage.getItem('countryCode');
      if (countryCode) {
        if (countryCode === 'KR') {
          setCountryKorea();
        } else {
          setCountryVietnam();
        }
        return;
      }
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        const country = data.country_code;
        localStorage.setItem('countryCode', country);
        if (country === 'KR') {
          setCountryKorea();
        } else {
          setCountryVietnam();
        }
      } catch (error) {
        console.error('Error fetching country data:', error);
      }
    }
  }, [setCountryVietnam, setCountryKorea]);

  useEffect(() => {
    setInitLanguage();
  }, [setInitLanguage]);

  const setInitOrderSupportConfig = useCallback(
    async me => {
      const { phoneCountry } = me;
      const configs = await Api.orderSupport.getConfigs({ phoneCountry });
      const checkLists = await Api.orderSupport.getCheckLists();
      if (configs.success) dispatch(setOrderSupportConfig(configs.data));
      if (checkLists.success) dispatch(setOrderSupportCheckList(checkLists.data));
    },
    [dispatch]
  );

  const setInitFieldListsConfig = useCallback(async () => {
    const fieldList = [];
    const result = await Api.user.getFieldAllList({ page: 1 });
    if (result.data) {
      fieldList.push(...result.data.items);
    }
    dispatch(setAllFieldList(fieldList));
  }, [dispatch]);

  const setInitUserInfo = useCallback(async () => {
    if (!token || loadingRef.current) return null;
    try {
      loadingRef.current = true;
      const result = await Api.auth.me();
      if (result.data) {
        if (result.data.phoneCountry === '82') {
          setCountryKorea();
        } else {
          setCountryVietnam();
        }
        dispatch(updateMe(result.data));
        await setInitOrderSupportConfig(result.data);
        dispatch(setDefaultField(result.data?.partner?.materialBuyer?.defaultField));
        await setInitFieldListsConfig(dispatch);
        checkPermission(result.data, location.pathname, navigate);
      } else {
        dispatch(logoutUser());
      }
    } catch (e) {
      console.log(e);
    } finally {
      loadingRef.current = false;
    }
  }, [dispatch, token, location.pathname, navigate, setCountryVietnam, setCountryKorea, setInitOrderSupportConfig, setInitFieldListsConfig]);

  return {
    user,
    token,
    locale,
    location,
    setInitUserInfo,
  };
};

export default useMainApp;
